/** Pure HUD sprite selection for hearts and hunger under status effects. */
import { FX } from '../game/effects/effects';

export type HeartStyle = 'normal' | 'poisoned' | 'withered' | 'frozen';

/** Wither beats poison (vanilla Gui.HeartType.forPlayer order: poison, wither, frozen). */
export function heartStyleOf(has: (id: number) => boolean, freezing = false): HeartStyle {
  if (has(FX.WITHER)) return 'withered';
  if (has(FX.POISON)) return 'poisoned';
  if (freezing) return 'frozen';
  return 'normal';
}

/** Sprite name under hud/heart/ for one heart, '' when empty. `hp` is that heart's health (0, 1, 2+). */
export function heartSprite(style: HeartStyle, hp: number, blinking = false): string {
  if (hp <= 0) return '';
  const base = style === 'normal' ? '' : style + '_';
  return `heart/${base}${hp >= 2 ? 'full' : 'half'}${blinking ? '_blinking' : ''}`;
}

/** Hunger bar sprites: the green-tinted set under Hunger. */
export function foodSprite(hungry: boolean, f: number): string {
  const suffix = hungry ? '_hunger' : '';
  return f >= 2 ? `food_full${suffix}` : f === 1 ? `food_half${suffix}` : '';
}
export const foodEmptySprite = (hungry: boolean): string => (hungry ? 'food_empty_hunger' : 'food_empty');

/** Index of the heart that hops during the regeneration wave (vanilla: tick % ceil(maxHealth + 5)), or -1. */
export function regenHeart(tick: number, regen: boolean, maxHealth: number): number {
  if (!regen) return -1;
  return tick % Math.ceil(maxHealth + 5);
}

/** Durability bar: width 0..13 and colour; null when undamaged (vanilla Item.getBarColor). */
export function durabilityBar(dmg: number, max: number): { width: number; color: string } | null {
  if (!(max > 0) || dmg <= 0) return null;
  const f = Math.max(0, 1 - dmg / max);
  const hue = f / 3; // red -> green
  const h = hue * 6, c = 1;
  const x = 1 - Math.abs((h % 2) - 1);
  const [r, g, b] = h < 1 ? [c, x, 0] : [x, c, 0];
  const hex = (v: number): string => Math.round(v * 255).toString(16).padStart(2, '0');
  return { width: Math.max(1, Math.round(13 - (dmg * 13) / max)), color: `#${hex(r)}${hex(g)}${hex(b)}` };
}
